import { cn } from "@/lib/utils/index";
import { Switch } from "@headlessui/react";
import { forwardRef } from "react";

interface FetchToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  color?: "primary" | "success" | "warning" | "destructive";
  label?: string;
  description?: string;
  className?: string;
  /** Accessible name for the switch when no visible `label` is rendered — e.g. a toggle placed next to its own heading. Overrides the default "Toggle switch" sr-only text. */
  ariaLabel?: string;
}

/**
 * Nowadays themed toggle switch.
 */
export const FetchToggle = forwardRef<HTMLButtonElement, FetchToggleProps>(
  (
    { checked, onChange, disabled = false, size = "md", color = "primary", label, description, className, ariaLabel },
    ref
  ) => {
    // Size configurations
    const sizeConfig = {
      sm: {
        switch: "w-8 h-4",
        dot: "h-3 w-3",
        translate: "translate-x-4",
        labelText: "text-sm",
        descriptionText: "text-xs",
      },
      md: {
        switch: "w-11 h-6",
        dot: "h-5 w-5",
        translate: "translate-x-5",
        labelText: "text-base",
        descriptionText: "text-sm",
      },
      lg: {
        switch: "w-14 h-7",
        dot: "h-6 w-6",
        translate: "translate-x-7",
        labelText: "text-lg",
        descriptionText: "text-base",
      },
    };

    // Color configurations
    const colorConfig = {
      primary: {
        enabled: "bg-[#5261EB]",
        disabled: "bg-[#5261EB]/50",
        hover: "hover:bg-[#4652D1]",
      },
      success: {
        enabled: "bg-emerald-500",
        disabled: "bg-emerald-500/50",
        hover: "hover:bg-emerald-600",
      },
      warning: {
        enabled: "bg-amber-500",
        disabled: "bg-amber-500/50",
        hover: "hover:bg-amber-600",
      },
      destructive: {
        enabled: "bg-red-500",
        disabled: "bg-red-500/50",
        hover: "hover:bg-red-600",
      },
    };

    const currentSize = sizeConfig[size];
    const currentColor = colorConfig[color];

    return (
      <Switch.Group>
        <div className={cn("flex items-center gap-3", className)}>
          <Switch
            ref={ref}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            aria-label={ariaLabel}
            className={cn(
              "relative inline-flex flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-opacity-75",
              currentSize.switch,
              checked ? cn(currentColor.enabled, !disabled && currentColor.hover) : "bg-gray-200",
              disabled && "cursor-not-allowed opacity-50"
            )}
          >
            <span className="sr-only">Toggle switch</span>
            <span
              aria-hidden="true"
              className={cn(
                "pointer-events-none inline-block transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out",
                currentSize.dot,
                "absolute left-[2px] top-[2px]",
                checked && currentSize.translate
              )}
            />
          </Switch>

          {(label || description) && (
            <div className="flex flex-col">
              {label && (
                <Switch.Label
                  className={cn(
                    "font-medium text-gray-900 select-none cursor-pointer",
                    currentSize.labelText,
                    disabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {label}
                </Switch.Label>
              )}
              {description && (
                <Switch.Description
                  className={cn(
                    "text-gray-500 select-none font-medium",
                    currentSize.descriptionText,
                    disabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {description}
                </Switch.Description>
              )}
            </div>
          )}
        </div>
      </Switch.Group>
    );
  }
);

FetchToggle.displayName = "FetchToggle";
